import Decimal from 'decimal.js'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isEqual } from 'lodash'
import checkValidReceiptNumberInput from '@/modules/receipt/_helpers/checkValidReceiptNumberInput'
import { CHART_OF_ACCOUNT_SKR_TYPE } from '@/modules/settings/components/tax-settings/chart-of-accounts/constants'
import { useSelector } from 'react-redux'
import { createReviewPayeeState, validateReviewPayee } from '@/modules/receipt/review/review-payee-state'

export interface ReceiptStateInterface {
  data: any,
  receipts: any[],
  receiptsRepository: {
    delete: string[]
  },
  isFormValid: boolean,
  showUploadHint: boolean,
  isFileContainerOpen: boolean,
  filesToAssign: any[],
}

const initialState: ReceiptStateInterface = {
  data: {},
  receipts: [],
  receiptsRepository: {
    delete: [],
  },
  isFormValid: false,
  isFileContainerOpen: false,
  showUploadHint: true,
  filesToAssign: [],
}

const validateForm = (data) => {
  const { priceTotal, payee, receiptNumber, positions, currentSkrType } = data
  // FLAG:useDocuAi — `reviewPayee` is present in the form data EXACTLY when the review component
  // created it (`build-review-data.ts` → `applyReviewPayeeToReceiptData`); a normal expense/revenue
  // form never carries the key. The line below has always made the same assumption — here it merely
  // gets a name.
  const isDocumentInvoiceReview = !!data.reviewPayee
  const reviewPayeeInvalid = data.reviewPayee
    ? !validateReviewPayee(createReviewPayeeState(data.reviewPayee)).isValid
    : false

  const gwgPosition = positions?.find(
    (pos) =>
      ((pos.bookkeepingAccountNo === 480 && currentSkrType === CHART_OF_ACCOUNT_SKR_TYPE.skr03) || (pos.bookkeepingAccountNo === 670 && CHART_OF_ACCOUNT_SKR_TYPE.skr04))
      && pos.bookkeepingAccountId != null,
  )

  // FLAG:useDocuAi — the review accepts receipt values AS THEY ARE, just as the path without the
  // flag does: there the user reviews in the drawer's Details tab (`incoming-invoice-details.tsx`),
  // a pure field form without amount validation whose save is gated only on `readOnly`. The
  // positivity rule belongs to the EXPENSE form; the review component inherited it only because it
  // IS the same `ReceiptEdit`. The result was a REVIEW BAN: a credit note/cancellation invoice with
  // negative totals (UNTDID 384 is the normal German case) rendered fully, but „Speichern" stayed
  // grey in ALL three hosts — it could not be reviewed anywhere.
  //
  // And the save no longer stops after the review: since PO 2026-09-15 the server turns a receipt
  // whose STATED TOTAL is negative around as a whole (`toExpenseAmount`), leaving the relative signs
  // inside it — a Leergut credit, a line discount — exactly as printed. So a Storno reviewed here is
  // created AND booked, on whichever ledger the booking target names. `hasNegativeReviewedTotals`
  // (receiptReviewCaseService) never blocked that registration — it only tells a credit note with
  // negative totals apart from one whose amounts are printed POSITIVE, where the sign says nothing
  // and the server still refuses (`cancellationSignUnknown`).
  //
  // `!priceTotal` remains for BOTH paths — 0 means "nothing extracted" here, not
  // "receipt totaling €0.00", and an empty extraction result must not be saved as reviewed.
  return !(!priceTotal
    || (!isDocumentInvoiceReview && new Decimal(parseFloat(priceTotal)).lte(0))
    || !payee
    || !receiptNumber
    || !checkValidReceiptNumberInput(receiptNumber)
    || reviewPayeeInvalid
    || gwgPosition?.totalNet < 250.01
    || gwgPosition?.totalNet > 1000.00
  )
}

const receiptSlice = createSlice({
  name: 'receipt',
  initialState,
  reducers: {
    deleteReceipt(state, action: PayloadAction<any>) {
      const receiptToDelete = action.payload
      const receipts = [...state.receipts].filter(receipt => { return receipt.id !== receiptToDelete.id })
      const receiptsRepository = {
        ...state.receiptsRepository,
        delete: [...state.receiptsRepository.delete, receiptToDelete],
      }
      state.receipts = receipts
      state.receiptsRepository = receiptsRepository
    },
    setFormValidation(state, action: PayloadAction<boolean>) {
      state.isFormValid = action.payload
    },
    setAndValidateData(state, action: PayloadAction<any>) {
      const newData = { ...state.data, ...action.payload }
      state.data = newData
      state.isFormValid = validateForm(newData)
    },
    resetData(state) {
      state.data = {}
      state.isFormValid = false
    },
    showFileContainer(state) {
      state.isFileContainerOpen = true
    },
    hideFileContainer(state) {
      state.isFileContainerOpen = false
    },
    hideUploadHint(state) {
      state.showUploadHint = false
    },
    assignFile(state, action: PayloadAction<any>) {
      const toAssign = action.payload
      const index = state.filesToAssign.findIndex(file => file.id === toAssign.id)
      if (index !== -1) {
        if (isEqual(toAssign, state.filesToAssign[index])) {
          return
        } else {
          state.filesToAssign[index] = toAssign
        }
      } else {
        state.filesToAssign.push(toAssign)
      }
    },
    assignFiles(state, action: PayloadAction<any>) {
      const filesToAssign = action.payload

      const updatedFiles = [...state.filesToAssign]
      for (const fileToAssign of filesToAssign) {
        const index = updatedFiles.findIndex(file => file.id === fileToAssign.id)
        if (index !== -1) {
          if (isEqual(fileToAssign, updatedFiles[index])) {
            return
          } else {
            updatedFiles[index] = fileToAssign
          }
        } else {
          updatedFiles.push(fileToAssign)
        }
      }

      state.filesToAssign = updatedFiles
    },
    unassignFile(state, action: PayloadAction<any>) {
      const toUnassign = action.payload
      const index = state.filesToAssign.findIndex(file => file.id === toUnassign.id)
      if (index === -1) {
        return
      }
      state.filesToAssign.splice(index, 1)
    },
    clearFiles(state) {
      state.filesToAssign = []
    },
    clearData(state) {
      // Deliberate historic no-op (reassigning the Immer draft parameter is discarded, so this does
      // NOT clear the slice) — kept byte-identical to the f2ac6a682f baseline. The legacy
      // expense/revenue cancel+save call sites rely on this: their form data is cleared on unmount
      // via resetData, and clearing receipts/filesToAssign here would change flag-OFF behaviour. The
      // real reset for the DocuAI review flow lives in clearReviewData below.
      state = { ...initialState }
    },
    // FLAG:useDocuAi — real reset for the receipt-review flow: a stale form position left in the
    // slice by the expense editing surface would otherwise bleed into the invoice review finalData.
    // Dispatched ONLY from the flag-ON review sites (expense-edit-page DMS-upload + embedded review
    // save-success), never from the legacy cancel/save paths, so flag-OFF stays baseline-identical.
    clearReviewData(state) {
      state.data = {}
      state.receipts = []
      state.receiptsRepository = { delete: [] }
      state.filesToAssign = []
      state.isFormValid = false
    },
  },
})

export const {
  deleteReceipt,
  setFormValidation,
  setAndValidateData,
  resetData,
  showFileContainer,
  hideFileContainer,
  hideUploadHint,
  assignFile,
  assignFiles,
  unassignFile,
  clearFiles,
  clearData,
  clearReviewData,
} = receiptSlice.actions
export default receiptSlice.reducer
